import { Card, Col, Layout, Row, Typography, Tag } from "antd";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Activity, BarChart3, ClipboardList, FileBarChart, FileSpreadsheet, Monitor, Trophy } from "lucide-react";
import { getKeycloakRealm } from "@/components/helpers";
import { useGetTenantApiKey, useGetTenantSettings } from "@hooks";
import { User } from "@types";

interface ReportCard {
  key: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  path?: string;
  externalUrl?: string;
  enabled: boolean;
  disabled: boolean;
}

interface ReportsHubPageProps {
  user: User;
}

export const ReportsHubPage = ({ user }: ReportsHubPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: tenantSettings } = useGetTenantSettings(true);
  const { data: tenantApiKey } = useGetTenantApiKey(true);
  const hasAdminRole = user.applicationRoles.some((role) => role.name === "Admin");

  const leaderboardUrl = tenantApiKey?.key
    ? `https://leaderboard.performa365.com/${getKeycloakRealm}/${tenantApiKey.key}`
    : undefined;

  const reportCards: ReportCard[] = [
    {
      key: "basic",
      titleKey: "reportsHub.basic.title",
      descriptionKey: "reportsHub.basic.description",
      icon: <BarChart3 size={48} />,
      path: `/${getKeycloakRealm}/reports/basic`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableBasicReport),
    },
    {
      key: "levelD",
      titleKey: "reportsHub.levelD.title",
      descriptionKey: "reportsHub.levelD.description",
      icon: <ClipboardList size={48} />,
      path: `/${getKeycloakRealm}/reports/leveld`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableLevelDReport),
    },
    {
      key: "tournament",
      titleKey: "reportsHub.tournament.title",
      descriptionKey: "reportsHub.tournament.description",
      icon: <Trophy size={48} />,
      path: `/${getKeycloakRealm}/reports/tournaments`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableTournamentReport),
    },
    {
      key: "levelC",
      titleKey: "reportsHub.levelC.title",
      descriptionKey: "reportsHub.levelC.description",
      icon: <FileBarChart size={48} />,
      path: `/${getKeycloakRealm}/reports/levelc`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableLevelCReport),
    },
    {
      key: "levelB",
      titleKey: "reportsHub.levelB.title",
      descriptionKey: "reportsHub.levelB.description",
      icon: <FileSpreadsheet size={48} />,
      path: `/${getKeycloakRealm}/reports/levelb`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableLevelBReport),
    },
    {
      key: "quizStatistics",
      titleKey: "reportsHub.quizStatistics.title",
      descriptionKey: "reportsHub.quizStatistics.description",
      icon: <Activity size={48} />,
      path: `/${getKeycloakRealm}/reports/quiz-statistics`,
      enabled: true,
      disabled: Boolean(tenantSettings?.disableQuizStatisticsReport),
    },
    {
      key: "leaderboard",
      titleKey: "reportsHub.leaderboard.title",
      descriptionKey: "reportsHub.leaderboard.description",
      icon: <Monitor size={48} />,
      externalUrl: leaderboardUrl,
      enabled: Boolean(leaderboardUrl),
      disabled: Boolean(tenantSettings?.disableLeaderboardReport),
    },
  ];

  const visibleReportCards = hasAdminRole
    ? reportCards.filter((card) => !card.disabled)
    : [];

  const handleCardClick = (card: ReportCard) => {
    if (!card.enabled) return;
    if (card.externalUrl) {
      window.open(card.externalUrl, "_blank", "noopener,noreferrer");
    } else if (card.path) {
      navigate(card.path);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Layout>
        <div style={{ display: "flex", marginBottom: 24 }}>
          <div
            style={{
              width: 3,
              backgroundColor: "#F1B80E",
              borderRadius: 100,
              marginRight: 12,
            }}
          />
          <Typography.Title level={2} style={{ margin: 0 }}>
            {t("reports")}
          </Typography.Title>
        </div>

        <Row gutter={[24, 24]}>
          {visibleReportCards.map((card) => (
            <Col xs={24} sm={12} lg={6} key={card.key}>
              <motion.div
                whileHover={card.enabled ? { scale: 1.02 } : undefined}
                whileTap={card.enabled ? { scale: 0.98 } : undefined}
              >
                <Card
                  hoverable={card.enabled}
                  onClick={() => handleCardClick(card)}
                  style={{
                    height: "100%",
                    cursor: card.enabled ? "pointer" : "not-allowed",
                    opacity: card.enabled ? 1 : 0.6,
                  }}
                  styles={{
                    body: {
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      padding: 24,
                      minHeight: 200,
                    },
                  }}
                >
                  <div
                    style={{
                      color: card.enabled ? "#F1B80E" : "#999",
                      marginBottom: 16,
                    }}
                  >
                    {card.icon}
                  </div>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: 0,
                      marginBottom: 8,
                      color: card.enabled ? undefined : "#999",
                    }}
                  >
                    {t(card.titleKey)}
                  </Typography.Title>
                  <Typography.Text
                    type="secondary"
                    style={{
                      marginBottom: 12,
                    }}
                  >
                    {t(card.descriptionKey)}
                  </Typography.Text>
                  {!card.enabled && (
                    <Tag color="default" style={{ marginTop: "auto" }}>
                      {t("reportsHub.comingSoon")}
                    </Tag>
                  )}
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>
      </Layout>
    </motion.div>
  );
};
